<template>
	<section class="cam-box" aria-label="Your team with Cam">
		<div class="columns is-variable is-5">
			<div class="column is-5-tablet">
				<div class="box has-no-shadow mb-0 py-5 px-4 capacity-dial-panel">
					<CapacityDial :capacity="capacity" />
				</div>
			</div>

			<div class="column is-7-tablet capacity-right-column">
				<div
					class="is-flex is-flex-direction-column is-justify-content-space-between capacity-pane"
					:class="{ 'is-hidden-pane': isEditingAssumptions }"
					:inert="isEditingAssumptions || null"
				>
					<div>
						<p class="title is-3 mb-2 capacity-outcome" aria-live="polite">
							<template v-if="outcome.kind === 'none' && capacity.share === 0">
								Move the slider to see what Cam could add to your team.
							</template>
							<template v-else-if="outcome.kind === 'none'">
								With these assumptions, Cam adds no extra tickets.
							</template>
							<template v-else-if="outcome.kind === 'developers'">
								Cam could give your team the extra capacity of <em>{{ outcome.label }}</em>.
							</template>
							<template v-else>
								Cam could give your team around <em>{{ outcome.label }}</em> of extra capacity every month.
							</template>
						</p>

						<p v-if="outcome.kind !== 'none'" class="mb-2 capacity-support">
							That’s around <strong>{{ formattedMore }} extra tickets a month</strong>, including the work that keeps getting pushed back.
						</p>

						<p v-if="outcome.kind !== 'none'" class="is-size-7 has-text-grey mb-0">
							Based on real teams’ performance in Gearset.
						</p>
					</div>

					<div class="mt-4">
						<ShareSlider class="mb-2" />

						<div class="has-text-right">
							<a
								ref="assumptionsLink"
								href="#cam-assumptions"
								class="is-size-7 has-text-grey capacity-assumptions-link"
								@click.prevent="openAssumptions"
							>
								Change assumptions
							</a>
						</div>
					</div>
				</div>

				<Assumptions
					ref="assumptions"
					class="capacity-pane"
					:class="{ 'is-hidden-pane': !isEditingAssumptions }"
					:inert="!isEditingAssumptions || null"
					@close="closeAssumptions"
				/>
			</div>
		</div>
	</section>
</template>

<script>

import Assumptions from './Assumptions.vue';
import CapacityDial from './CapacityDial.vue';
import ShareSlider from './ShareSlider.vue';

import { describeOutcome, formatNumber } from '../calculations.js';

export default {
	components: {
		Assumptions,
		CapacityDial,
		ShareSlider,
	},

	props: {
		capacity: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			isEditingAssumptions: false,
		};
	},

	computed: {
		outcome() {
			return describeOutcome(this.capacity);
		},

		formattedMore() {
			return formatNumber(this.capacity.more);
		},
	},

	methods: {
		/* The control sits in the pane that becomes inert. The browser moves
		   focus off that control on a later task, so focus the destination
		   after that or the move wins and focus drops to the document. */
		openAssumptions() {
			this.isEditingAssumptions = true;
			this.focusAfterPaneChange(() => {
				this.$refs.assumptions.focusFirstField();
			});
		},

		closeAssumptions() {
			this.isEditingAssumptions = false;
			this.focusAfterPaneChange(() => {
				this.$refs.assumptionsLink.focus();
			});
		},

		focusAfterPaneChange(focus) {
			this.$nextTick(() => {
				window.setTimeout(focus);
			});
		},
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.capacity-dial-panel {
	border-radius: 1rem;
}

.capacity-right-column {
	@include tablet {
		display: grid;
	}
}

.capacity-pane {
	@include tablet {
		grid-area: 1 / 1;

		&.is-hidden-pane {
			visibility: hidden;
			pointer-events: none;
		}
	}

	@include mobile {
		&.is-hidden-pane {
			display: none !important;
		}
	}
}

.capacity-outcome {
	line-height: 1.25;
}

.capacity-support {
	font-variant-numeric: tabular-nums;
}

.capacity-assumptions-link {
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: $navy !important;
	}
}

</style>
